/**
 * =============================================================================
 * ROASTBREW — VIDEO VIEW (TABLET)
 * =============================================================================
 *
 * DESKTOP LAYOUT, MOBILE PLAYER.
 *
 * A tablet has a desktop-sized screen and a phone's input. Before this file
 * existed, VideoViewRouter only tested `isMobile` (width < 768), so an iPad at
 * 768–1023px fell through to the desktop page — hover-dependent controls, ~27px
 * tap targets, and a 4-tab strip that needs ~107px per tab in an 80px slot.
 *
 * So this page takes:
 *   LAYOUT  — verbatim from VideoViewPageFairPlay: the two-column grid,
 *             the tab strip, TopicRow, the course navigator, the whole page shell.
 *             That look was worked out carefully and is unchanged here.
 *   PLAYER  — verbatim from VideoViewPageFairPlayMobile: the touch control layer
 *             AND the DRM engine (FairPlay, via Video.js + videojs-contrib-eme over native HLS).
 *
 * ── THE PLAYER IS COPIED, NOT REWRITTEN ──────────────────────────────────────
 * MobilePlayer below is byte-identical to the one in VideoViewPageFairPlayMobile.jsx. It brings the
 * per-control tap-guard, 48px targets, the bottom-sheet speed picker, the
 * always-interactive tap layer (iOS does not bubble clicks off a <video>), and
 * pointerEvents:'none' on every overlay that covers the player.
 *
 * ⚠️ THREE COPIES OF EACH ENGINE NOW EXIST — desktop, mobile and tablet.
 * A DRM CHANGE MUST BE APPLIED TO ALL THREE:
 *   VideoViewPageFairPlay.jsx (desktop) and VideoViewPageFairPlayMobile.jsx (mobile)
 *   and this file.
 * This is scaffolding. The end state is one page per engine with a swappable
 * control layer; until then the duplication is the price of not destabilising
 * a working desktop page.
 *
 * ── PROPS ────────────────────────────────────────────────────────────────────
 * AppLayout clones isMobile/isTablet/isDesktop/sidebarOpen onto this component.
 * The layout below reads isMobile for its own breakpoints and receives `false`,
 * which is correct: a tablet should lay out like a desktop.
 * =============================================================================
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-eme';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { isLocked, isLightTheme, tierRank } from './config';
import UpgradePrompt from './UpgradePrompt';
import { videoCompleted, videoPct, nodePct, videoResume } from './data/progress';
import SearchBar from './SearchBar';
import FairPlayPlayer from './FairPlayPlayers';
import { useCourseContext, CourseNavigatorView, NavigatorDrawer, NavigatorColumn, UpNextButton, useWindowSize, PhoneNavigatorDrawer, TitleStack } from './CourseNavigator';
import { endCardInfo } from './EndOfLessonCard';
import { usePageSize, useVideoCap, useElementHeight, VIDEO_CAP_TABS } from './videoViewLayout';
import useCDNToken from './useCDNToken';

// Deterministic per-lesson gradient. The API no longer stores gradient colors;
// instead we derive a stable pair from the videoId so every lesson keeps a
// consistent look across sessions without any stored data. Hues are spread
// across the wheel; saturation/lightness kept muted so it sits behind the
// player without clashing with the gold/cream theme. Returns [from, to] hex.
function gradientFor(id) {
  const s = String(id || '');
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0; // djb2
  const hue1 = h % 360;
  const hue2 = (hue1 + 40) % 360;               // nearby hue for a smooth blend
  const hsl = (deg, sat, light) => {
    // minimal HSL→hex (no deps)
    const a = sat * Math.min(light, 1 - light);
    const f = (n) => {
      const k = (n + deg / 30) % 12;
      const c = light - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
      return Math.round(255 * c).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };
  return [hsl(hue1, 0.45, 0.45), hsl(hue2, 0.5, 0.35)];
}

// Locate a topic across the catalog by its videoId (the join key).
function findTopicInCatalog(catalog, videoId) {
  for (const su of (catalog || [])) {
    for (const c of (su.courses || [])) {
      for (const ch of (c.chapters || [])) {
        for (const sec of (ch.sections || [])) {
          for (const t of (sec.topics || [])) {
            if (t.videoId === videoId) return { topic: t, course: c, section: sec };
          }
        }
      }
    }
  }
  return null;
}

// ── The pending-lesson shell ─────────────────────────────────────────────────
// Built from the CATALOG ONLY. This used to spread PLACEHOLDER_VIDEO, which meant
// a lesson whose /video/:id request had not landed — or had FAILED — rendered a
// fabricated one: someone else's description, someone else's key topics, and a
// video URL that could never play. On failure that was indistinguishable from a
// real lesson, so an outage looked like working software.
//
// Now every rich field is EMPTY and only the identity is real, taken from the
// catalog the app already holds: the correct title, course and tier appear while
// /video/:id is in flight, and nothing is invented.
//
// SHAPE IS LOAD-BEARING — this must be the complete four-slot object from
// CONTRACT.md §2, not a partial one. Several reads are unguarded:
//   VideoViewPage/FairPlay line ~728   {video.series.name}     — needs series.name
//   VideoViewPage/FairPlay line ~984   {video.keyTopics.map()} — needs keyTopics []
// A partial shell white-screens the player. Keep every key below.
function buildFallbackVideo(catalog, id) {
  const vid = id || '';
  const base = {
    id: vid,
    slug: '',
    title: '',
    description: '',
    keyTopics: [],
    series: { name: '' },
    tierRequired: 'free',
    video:          { id: vid, url: null, thumbnailUrl: null, durationSeconds: 0 },
    homework:       { pdfUrl: null, pages: 0 },
    answerKey:      { pdfUrl: null, pages: 0 },
    answerKeyVideo: { id: vid ? vid + '-ak' : '', url: null, thumbnailUrl: null, durationSeconds: 0 },
  };
  const hit = id ? findTopicInCatalog(catalog, id) : null;
  if (!hit) return base;
  return {
    ...base,
    slug: hit.topic.slug || '',
    title: hit.topic.name || '',
    series: { name: hit.course ? hit.course.name : '' },
    tierRequired: hit.topic.tierRequired,
    video: { ...base.video, thumbnailUrl: hit.topic.thumbnailUrl || null },
  };
}
const getVolumeStyles = (t) => {
  if (tierRank(t)===0) return {background:'#8B8B7A'};
  if (tierRank(t)===1) return {background:'linear-gradient(90deg,#C9A962,#E8D5A3)',boxShadow:'0 0 8px rgba(201,169,98,0.4)'};
  return {background:'linear-gradient(90deg,#E8D5A3,#FFF8E7)',boxShadow:'0 0 10px rgba(232,213,163,0.5)'};
};

function applyVariantOverride(url) {
  if (!url) return url;
  // ── ?testSrc= — load an ARBITRARY manifest, bypassing the CDN entirely. ──────
  // Diagnostic only. Purpose: prove whether this player can drive a multi-variant
  // master playlist WITH a separate audio rendition group at all, using a known-good
  // UNENCRYPTED stream. If a reference stream also freezes, the fault is in the
  // player/contrib-eme setup, not in DRM or our packaging — and vice versa.
  //   ?testSrc=apple   -> Apple's public bipbop advanced fMP4 example (multi-variant + audio group)
  //   ?testSrc=<url>   -> any https .m3u8 you paste (URL-encode it)
  const TEST_SRCS = {
    apple: 'https://devstreaming-cdn.apple.com/videos/streaming/examples/img_bipbop_adv_example_fmp4/master.m3u8',
  };
  try {
    const t = new URLSearchParams(window.location.search).get('testSrc');
    if (t) {
      const chosen = TEST_SRCS[t] || (/^https:\/\/[^\s]+\.m3u8(\?|$)/i.test(t) ? t : null);
      if (chosen) {
        return chosen;
      }
    }
  } catch (e) { /* no search string — ignore */ }

  let variant = null;
  try { variant = new URLSearchParams(window.location.search).get('forceVariant'); } catch { return url; }
  // Two forms:
  //   ?forceVariant=v0           -> <base>/v0/video.m3u8   (bare video variant, no audio)
  //   ?forceVariant=audio/audio  -> <base>/audio/audio.m3u8 (any child playlist path)
  // The second form exists to pin the AUDIO rendition alone, which the original
  // video-only form could not reach (the filename was hardcoded to video.m3u8).
  if (!variant || !/^[a-z0-9_\-/]+$/i.test(variant) || variant.includes('..')) return url;
  const child = variant.includes('/') ? variant + '.m3u8' : variant + '/video.m3u8';
  const out = url.replace(/\/master\.m3u8(\?|$)/, '/' + child + '$1');
  return out;
}

// ============================================================================
// LAYOUT — copied verbatim from VideoViewPageFairPlay.jsx
// ============================================================================



// ============================================================================
// MAIN COMPONENT
// ============================================================================

// A "cold load" is a page opened with no prior user interaction this document
// (direct URL, refresh, new tab). Browsers block media from starting WITH SOUND
// until the user has produced a real gesture (click/key/tap) — a hover or page
// load doesn't count. navigator.userActivation.hasBeenActive is a sticky bit that
// is true once the page has been activated and survives in-app (SPA) navigation,
// so if a user clicked a card/link to get here it's already true and we autoplay
// with sound; on a cold load it's false, so we skip autoplay and let the existing
// Play button start it (that click is the gesture that unlocks audio).
function pageHasBeenActivated() {
  const ua = typeof navigator !== 'undefined' ? navigator.userActivation : null;
  return ua && typeof ua.hasBeenActive === 'boolean' ? ua.hasBeenActive : false;
}

export default function VideoViewPageFairPlayTablet({isMobile=false,isTablet=false,isDesktop=true,sidebarOpen=true}) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // [ROUTING] This is the FAIRPLAY (Safari) copy — Video.js + native-HLS FairPlay player.
  const { colors } = useTheme();
  const { profile, catalog, api, refreshAlgorithm, progressByVideo } = useAuth();
  const currentTier = profile?.currentTier || 'free';   // forwarded to sub-components (SkipButton, VideoPlayer, TopicRow) as a prop

  // URL drives everything: /video/:id?course=<courseId>
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const courseId = searchParams.get('course');
  const navigate = useNavigate();

  // VIDEO PAYLOAD — fetched on demand. Seeded from the CATALOG-ONLY shell so the
  // correct title, course and tier show while /video/:id is in flight, with every
  // rich field empty until the real payload lands.
  const [video,setVideo]=useState(()=>buildFallbackVideo(catalog, id));
  // Tracks whether the REAL /video/:id payload has loaded. The shell's video.url is
  // null by construction, so there is nothing unplayable to guard against any more —
  // but this still gates the engine, because a locked or not-yet-loaded lesson must
  // not be handed a URL.
  const [videoLoaded,setVideoLoaded]=useState(false);
  const videoLocked = isLocked(video.tierRequired, currentTier);
  const actualVideoUrl = (videoLoaded && !videoLocked && video.video && video.video.url) || null;
  const lessonResume = videoResume(progressByVideo, video.id);
  const akResume = videoResume(progressByVideo, video.answerKeyVideo ? video.answerKeyVideo.id : null);

  const [isPlaying,setIsPlaying]=useState(false);
  const [currentTime,setCurrentTime]=useState(0);
  const [akPlaying,setAkPlaying]=useState(false);   // answer-key video player (independent)
  const [akTime,setAkTime]=useState(0);
  const [leftTab,setLeftTab]=useState('video');
  // Link refresh (useCDNToken.js): keeps this lesson's CDN links fresh in state. Nothing here waits on it.
  const link = useCDNToken({ id, api, video, setVideo, ready: videoLoaded, isPlaying, currentTime, akPlaying, akTime, akOpen: leftTab === 'answerkey' });

  // `catalog` is used ONLY by buildFallbackVideo, but it used to sit in this dep
  // array — so when App's catalog fetch replaced the initial state, this refetched
  // /video/:id, a NEW token was minted, videoUrl changed identity, and the player
  // tore down mid-load. Read it from a ref: the shell still gets fresh catalog
  // data, but a catalog arrival no longer restarts playback.
  // NOTE: this matters MORE now — ensureFresh in App.jsx can refresh the catalog
  // mid-session to re-mint expiring signed URLs, so catalog identity changes more
  // often than it used to.
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;

  // Fetch the video payload on id change. On success we mark videoLoaded so the real
  // R2 URL can play; on failure we show the placeholder for metadata only (its sample
  // video URL is never played because videoLoaded stays false).
  useEffect(()=>{
    if(!id)return;
    let cancelled=false;
    setVideoLoaded(false);
    api.fetch('/video/'+id)
      .then((res)=>(res.ok?res.json():Promise.reject(new Error('video fetch failed'))))
      .then((body)=>{ if(!cancelled && body && body.data){ setVideo(body.data); setVideoLoaded(true); } })
      .catch((err)=>{ if(!cancelled){ console.error('Failed to load video:',err); setVideo(buildFallbackVideo(catalogRef.current, id)); setVideoLoaded(false); } });
    return ()=>{ cancelled=true; };
  },[id,api]);

  // Asset handlers wired to the payload.
  const handleOpenHomework=()=>link.openPdf('homework');
  const handleSubmitHomework=()=>{ console.log('[placeholder] submit homework'); };   // no submit target in the data yet
  const handleOpenAnswerKeyPdf=()=>link.openPdf('answerKey');
  const handleSelectTopic=(t)=>{ if(isLocked(t.tierRequired, currentTier)){ setUpgradeOpen(true); return; } navigate('/video/'+t.videoId+(courseId?'?course='+courseId:'')); };
  const changeCourse=(cid)=>{ const p=new URLSearchParams(searchParams); if(cid)p.set('course',cid); else p.delete('course'); setSearchParams(p); };

  // Navigation comes from the catalog (subjects -> courses). Flatten to courses.
  const { allCourses, ctxCourse, coursesForVideo, flatTopics, curIdx, upNext } = useCourseContext({ catalog, courseId, video });
  // ── Course Navigator (2.11): the column where the panel was (two columns), otherwise the drawer with its ribbon
  const [navOpen, setNavOpen] = useState(false);
  const [navSeq, setNavSeq] = useState(0);
  const winSize = useWindowSize();
  // 2.12e · tablet: always the drawer with its ribbon (never the column); under 744 px of content, the narrow layout
  const pageRef = useRef(null);
  const page = usePageSize(pageRef);
  const navLayout = page.size === 'small' ? 'phone' : 'drawer';
  const playerBoxRef = useRef(null);
  const videoCap = useVideoCap(playerBoxRef, page, video.id, VIDEO_CAP_TABS);   // the links under the video stay on screen too
  const navWidth = Math.round(winSize.width * (winSize.height >= winSize.width ? 0.7 : 0.45));
  const navSection = ctxCourse ? ctxCourse.chapters.flatMap((ch) => ch.sections).find((sec) => sec.topics.some((t) => t.videoId === video.id)) || null : null;
  const navPos = navSection ? navSection.topics.findIndex((t) => t.videoId === video.id) + 1 : 0;
  const navTip = ctxCourse && navSection ? `${ctxCourse.name} · Lesson ${navPos} of ${navSection.topics.length}` : undefined;
  const openNav = () => { setNavSeq((n) => n + 1); setNavOpen(true); };
  const navView = (mode) => <CourseNavigatorView mode={mode} video={video} catalog={catalog} courseId={courseId} ctxCourse={ctxCourse} coursesForVideo={coursesForVideo} progressByVideo={progressByVideo} currentTier={currentTier} colors={colors} isMobile={isMobile} handleSelectTopic={handleSelectTopic} changeCourse={changeCourse} onClose={() => setNavOpen(false)} openSeq={navSeq} />;
  // ── 2.12d · very small (under 744 px of content): the phone's shape, the desktop's behaviour ──
  // A fixed header row (AppLayout's <main> clips overflow, so sticky can't stick there): 70 px on the left for
  // AppLayout's own menu button, then the search (same row from 540 px of content, else its own row beneath, so
  // it keeps every control) and the Course button; a spacer of its measured height keeps content clear of it.
  const courseBtnRef = useRef(null);
  const [closeAt, setCloseAt] = useState({ top: 16, right: 20 });
  const openPhoneNav = () => { const b = courseBtnRef.current; if (b) { const r = b.getBoundingClientRect(); setCloseAt({ top: Math.round(r.top), right: Math.round(window.innerWidth - r.right) }); } openNav(); };
  // End of lesson card: the lesson and course details, plus its two actions (open the navigator; back to the course page)
  const endCard = { ...endCardInfo({ catalog, ctxCourse, curIdx, upNext, video }), onOpenNavigator: navLayout === 'phone' ? openPhoneNav : openNav, onBackToCourse: () => navigate(endCardInfo({ catalog, ctxCourse, curIdx, upNext, video }).coursePath) };
  const navChapter = ctxCourse && navSection ? ctxCourse.chapters.find((ch) => ch.sections.includes(navSection)) || null : null;
  const smallHeaderRef = useRef(null);
  const smallHeaderH = useElementHeight(smallHeaderRef, navLayout === 'phone', 76);
  const smallOneRow = page.width >= 540;
  const smallLine = isLightTheme(currentTier) ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const smallHeader = (<>
    <div ref={smallHeaderRef} data-small-header="" style={{position:'fixed',top:0,left:0,right:0,zIndex:30,padding:'16px 20px 16px 70px',background:colors.bgAlt,borderBottom:`1px solid ${smallLine}`}}>
      <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
        {smallOneRow ? <div style={{flex:1,minWidth:0}}><SearchBar variant="video" placeholder="Search courses, lessons, topics..." /></div> : <div style={{flex:1}} />}
        <button ref={courseBtnRef} type="button" aria-label="Open Course Navigator" onClick={openPhoneNav} aria-hidden={navOpen ? 'true' : undefined} tabIndex={navOpen ? -1 : 0}
          style={{visibility:navOpen?'hidden':'visible',flexShrink:0,height:'44px',padding:'0 14px',display:'inline-flex',alignItems:'center',gap:'9px',borderRadius:'12px',border:`1px solid ${smallLine}`,background:isLightTheme(currentTier)?'rgba(0,0,0,0.03)':'rgba(255,255,255,0.03)',color:colors.text,fontFamily:'"DM Sans",sans-serif',fontWeight:600,fontSize:'13.5px',cursor:'pointer'}}>
          Course<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="18" r="1" /></svg>
        </button>
      </div>
      {!smallOneRow && <div style={{marginTop:'12px',marginLeft:'-50px'}}><SearchBar variant="video" placeholder="Search courses, lessons, topics..." /></div>}
    </div>
    <div aria-hidden="true" style={{height:smallHeaderH+'px'}} />
  </>);

  // Autoplay only once the user has interacted with the page at all. On a fresh load
  // navigator.userActivation.hasBeenActive is FALSE, so this deliberately does nothing
  // and the video waits at HAVE_METADATA (preload="metadata") until play() is pressed.
  // That is by design — Safari rejects unmuted autoplay without a gesture anyway.
  useEffect(()=>{setCurrentTime(lessonResume);setAkPlaying(false);setAkTime(akResume);if(!pageHasBeenActivated())return undefined;const t=setTimeout(()=>setIsPlaying(true),800);return()=>clearTimeout(t);},[video.id]);

  // Single-player rule: starting either video pauses the other (last-pressed-wins).
  useEffect(()=>{if(isPlaying)setAkPlaying(false);},[isPlaying]);
  useEffect(()=>{if(akPlaying)setIsPlaying(false);},[akPlaying]);
  // Answer-key never autoplays: leaving its tab forces it paused, so returning to the
  // tab (or a refresh) remounts the player stopped instead of resuming a stale `true`.
  useEffect(()=>{if(leftTab!=='answerkey')setAkPlaying(false);},[leftTab]);

  useEffect(()=>{if(actualVideoUrl)return;let i;if(isPlaying)i=setInterval(()=>setCurrentTime(p=>p+1),1000);return()=>clearInterval(i);},[isPlaying,actualVideoUrl]);
  useEffect(()=>{document.body.setAttribute('data-video-page','true');return()=>document.body.removeAttribute('data-video-page');},[]);
  // Refresh the suggestion algorithm whenever the user LEAVES this page. The player
  // heartbeat has been streaming watch progress to the backend during playback, so on
  // unmount the server's freshly recomputed suggestions are pulled and Library/Home
  // reflect what was just watched. Cleanup only fires if the page actually mounted, so
  // this can't run unless the user was here; the ref keeps the call current without
  // re-arming the unmount handler on every token change.
  const refreshAlgoRef = useRef(refreshAlgorithm);
  useEffect(()=>{ refreshAlgoRef.current = refreshAlgorithm; });
  useEffect(()=>()=>{ if (refreshAlgoRef.current) refreshAlgoRef.current(); }, []);
  // On video->video navigation (not the initial mount — the app already loaded
  // /me/state at login), re-pull authoritative progress + the recomputed feed so
  // the left-bar progress/completion reflects what the heartbeats persisted.
  const didMountRef = useRef(false);
  useEffect(()=>{ if(!didMountRef.current){didMountRef.current=true;return;} if(refreshAlgoRef.current) refreshAlgoRef.current(); }, [video.id]);

  return (
    <div ref={pageRef} style={{padding:navLayout==='phone'?'0 20px 40px':'28px 48px',minHeight:'100vh',width:'100%',maxWidth:'100%',overflowX:'hidden',boxSizing:'border-box',background:colors.bg,animation:'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1)'}}>
      <div style={{position:'fixed',inset:0,background:!isLightTheme(currentTier)?`radial-gradient(ellipse at 50% 0%,${gradientFor(video.id)[0]}15 0%,transparent 50%),radial-gradient(ellipse at 100% 100%,${colors.accent}08 0%,transparent 50%)`:'none',pointerEvents:'none',zIndex:0}} />
      {/* Page search */}
      {navLayout === 'phone' ? smallHeader : (
      <div style={{margin:'-28px -48px 28px',padding:'24px 48px',background:colors.bgAlt,borderBottom:`1px solid ${isLightTheme(currentTier)?'rgba(0,0,0,0.06)':'rgba(255,255,255,0.06)'}`,position:'relative',zIndex:2,animation:'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)'}}>
        <div style={{maxWidth:'520px',margin:'0 auto'}}>
          <SearchBar variant="video" placeholder="Search courses, lessons, topics..." />
        </div>
      </div>
      )}
      {/* The page's entrance (October 15, 2026): the same fade-and-rise as the other pages, on the search band and the content.
          No fill mode, so no transform remains afterwards: the player's fixed popovers and the drawers need the viewport. */}
      <main style={{position:'relative',zIndex:1,animation:'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)'}}>
        <div style={{maxWidth:'1400px',margin:'0 auto',padding:'0',display:'grid',gridTemplateColumns:'minmax(0,1fr)',gap:isMobile?'24px':'40px'}}>
          <div>
            {/* The title stack on every layout (October 15, 2026): course · chapter · lesson with the arcs, centred above the video. */}
            <div style={{maxWidth:videoCap?videoCap+'px':undefined,margin:'0 auto'}}>{/* the same width as the video below, so the stack is centred over it */}
              <TitleStack course={ctxCourse && ctxCourse.name} chapter={navChapter && navChapter.name} lesson={video.title} colors={colors} large={navLayout !== 'phone'} />
            </div>
            <div ref={playerBoxRef} style={{maxWidth:videoCap?videoCap+'px':undefined,margin:'0 auto'}}>{/* 2.12c · the video cap: its control bar stays on screen; centred in its column when capped (October 15, 2026) */}
            <FairPlayPlayer device="tablet" refreshLink={link.refreshCDNToken} key={video.id} upNext={upNext} upNextLocked={!!upNext && isLocked(upNext.tierRequired, currentTier)} onUpNext={() => { if (upNext) handleSelectTopic(upNext); }} endCard={endCard} onUnlock={() => setUpgradeOpen(true)} video={video} videoUrl={actualVideoUrl} videoId={video.id} assetId={video.slug} resumeFrom={lessonResume} colors={colors} currentTier={currentTier} isPlaying={isPlaying} setIsPlaying={setIsPlaying} currentTime={currentTime} setCurrentTime={setCurrentTime} isMobile={isMobile} isTablet={isTablet}/>
            </div>
            <div style={{marginTop:isMobile?'20px':'28px'}}>
              <div style={{display:'flex',gap:'4px',padding:'4px',background:isLightTheme(currentTier)?'rgba(0,0,0,0.04)':'rgba(255,255,255,0.04)',borderRadius:'10px',margin:isMobile?'4px 0 20px':'8px 0 24px'}}>
                {[['video','Video'],['homework','Homework'],['answerkey','Answer Key Video'],['answerkeypdf','Answer Key PDF']].map(([k,label])=>(
                  <button key={k} onClick={()=>setLeftTab(k)} style={{flex:1,position:'relative',padding:isMobile?'10px 6px':'12px',background:leftTab===k?colors.cardBg:'transparent',border:'none',borderRadius:'8px',color:leftTab===k?colors.text:colors.muted,fontSize:isMobile?'11px':'13px',fontWeight:'500',fontFamily:'"DM Sans",sans-serif',cursor:'pointer',transition:'all 0.2s ease',boxShadow:leftTab===k?'0 2px 8px rgba(0,0,0,0.1)':'none',whiteSpace:'nowrap'}}>{label}
                    {/* A small chevron under the open tab (October 15, 2026): the content it opens sits below the bar. */}
                    {leftTab===k && <svg aria-hidden="true" width="14" height="8" viewBox="0 0 14 8" style={{position:'absolute',left:'50%',bottom:'-15px',transform:'translateX(-50%)',pointerEvents:'none'}}><path d="M1 1l6 5 6-5" fill="none" stroke={colors.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </button>
                ))}
              </div>
              {leftTab==='video' && <>
                <div style={{marginBottom:isMobile?'20px':'24px'}}><h3 style={{fontSize:isMobile?'16px':'18px',fontWeight:'400',color:colors.text,fontFamily:'"Cormorant Garamond",serif',marginBottom:'12px'}}>About This Episode</h3><p style={{fontSize:isMobile?'14px':'15px',color:colors.textSecondary,fontFamily:'"DM Sans",sans-serif',lineHeight:1.7,whiteSpace:'pre-line'}}>{video.description}</p></div>
                <div><h3 style={{fontSize:isMobile?'16px':'18px',fontWeight:'400',color:colors.text,fontFamily:'"Cormorant Garamond",serif',marginBottom:'12px'}}>Key Topics</h3><ul style={{listStyle:'none',padding:0,margin:0,display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(2,1fr)',gap:'10px'}}>{video.keyTopics.map((t,i)=>(<li key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:isMobile?'10px 14px':'12px 16px',background:isLightTheme(currentTier)?'rgba(0,0,0,0.03)':'rgba(255,255,255,0.03)',borderRadius:'8px',fontSize:isMobile?'12px':'13px',color:colors.text,fontFamily:'"DM Sans",sans-serif'}}><div style={{width:'6px',height:'6px',borderRadius:'50%',background:colors.accent,flexShrink:0}}/>{t}</li>))}</ul></div>
              </>}
              {leftTab==='homework' && (
                <div style={{padding:isMobile?'20px':'24px',background:isLightTheme(currentTier)?'rgba(0,0,0,0.02)':'rgba(255,255,255,0.02)',borderRadius:'12px',border:`1px solid ${isLightTheme(currentTier)?'rgba(0,0,0,0.06)':'rgba(255,255,255,0.06)'}`}}>
                  <h3 style={{fontSize:isMobile?'16px':'18px',fontWeight:'400',color:colors.text,fontFamily:'"Cormorant Garamond",serif',marginBottom:'8px'}}>Homework Assignment</h3>
                  <p style={{fontSize:isMobile?'14px':'15px',color:colors.textSecondary,fontFamily:'"DM Sans",sans-serif',lineHeight:1.7,marginBottom:'20px'}}>Complete the exercises for this lesson, then submit your work for review.</p>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'12px'}}>
                    <button onClick={handleOpenHomework} style={{display:'flex',alignItems:'center',gap:'8px',padding:isMobile?'10px 16px':'12px 20px',background:colors.accent,border:'none',borderRadius:'8px',color:isLightTheme(currentTier)?'#fff':'#000',fontSize:isMobile?'13px':'14px',fontWeight:'600',fontFamily:'"DM Sans",sans-serif',cursor:'pointer'}}><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>View Assignment</button>
                    <button onClick={handleSubmitHomework} style={{display:'flex',alignItems:'center',gap:'8px',padding:isMobile?'10px 16px':'12px 20px',background:'transparent',border:`1px solid ${colors.accent}`,borderRadius:'8px',color:colors.accent,fontSize:isMobile?'13px':'14px',fontWeight:'600',fontFamily:'"DM Sans",sans-serif',cursor:'pointer'}}><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>Submit Work</button>
                  </div>
                </div>
              )}
              {leftTab==='answerkey' && (
                <div style={{padding:isMobile?'20px':'24px',background:isLightTheme(currentTier)?'rgba(0,0,0,0.02)':'rgba(255,255,255,0.02)',borderRadius:'12px',border:`1px solid ${isLightTheme(currentTier)?'rgba(0,0,0,0.06)':'rgba(255,255,255,0.06)'}`}}>
                  <h3 style={{fontSize:isMobile?'16px':'18px',fontWeight:'400',color:colors.text,fontFamily:'"Cormorant Garamond",serif',marginBottom:'12px'}}>Answer Key Video</h3>
                  {video.answerKeyVideo?.url
                    ? <FairPlayPlayer device="tablet" refreshLink={link.refreshCDNToken} key={'ak-'+video.id} video={video} videoUrl={video.answerKeyVideo.url} videoId={video.answerKeyVideo.id} assetId={video.slug} resumeFrom={akResume} colors={colors} currentTier={currentTier} isPlaying={akPlaying} setIsPlaying={setAkPlaying} currentTime={akTime} setCurrentTime={setAkTime} isMobile={isMobile} isTablet={isTablet}/>
                    : <p style={{fontSize:isMobile?'13px':'14px',color:colors.muted,fontFamily:'"DM Sans",sans-serif'}}>No answer-key video for this lesson.</p>}
                  <p style={{fontSize:isMobile?'13px':'14px',color:colors.muted,fontFamily:'"DM Sans",sans-serif',marginTop:'12px'}}>Full worked solution and walkthrough.</p>
                </div>
              )}
              {leftTab==='answerkeypdf' && (
                <div style={{padding:isMobile?'20px':'24px',background:isLightTheme(currentTier)?'rgba(0,0,0,0.02)':'rgba(255,255,255,0.02)',borderRadius:'12px',border:`1px solid ${isLightTheme(currentTier)?'rgba(0,0,0,0.06)':'rgba(255,255,255,0.06)'}`}}>
                  <h3 style={{fontSize:isMobile?'16px':'18px',fontWeight:'400',color:colors.text,fontFamily:'"Cormorant Garamond",serif',marginBottom:'8px'}}>Answer Key (PDF)</h3>
                  <p style={{fontSize:isMobile?'14px':'15px',color:colors.textSecondary,fontFamily:'"DM Sans",sans-serif',lineHeight:1.7,marginBottom:'20px'}}>Written worked solutions you can open or download.{video.answerKey?.pages?` (${video.answerKey.pages} pages)`:''}</p>
                  {video.answerKey?.pdfUrl
                    ? <button onClick={handleOpenAnswerKeyPdf} style={{display:'flex',alignItems:'center',gap:'8px',padding:isMobile?'10px 16px':'12px 20px',background:colors.accent,border:'none',borderRadius:'8px',color:isLightTheme(currentTier)?'#fff':'#000',fontSize:isMobile?'13px':'14px',fontWeight:'600',fontFamily:'"DM Sans",sans-serif',cursor:'pointer'}}><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>Open Answer Key</button>
                    : <p style={{fontSize:isMobile?'13px':'14px',color:colors.muted,fontFamily:'"DM Sans",sans-serif'}}>No answer-key PDF for this lesson.</p>}
                </div>
              )}
            </div>
          </div>
          {navLayout === 'column'
            ? <NavigatorColumn colors={colors} light={isLightTheme(currentTier)}>{navView('column')}<UpNextButton upNext={upNext} handleSelectTopic={handleSelectTopic} colors={colors} currentTier={currentTier} isMobile={isMobile} /></NavigatorColumn>
            : <div><UpNextButton upNext={upNext} handleSelectTopic={handleSelectTopic} colors={colors} currentTier={currentTier} isMobile={isMobile} /></div>}
        </div>
      </main>
      {navLayout === 'drawer' && (
        <NavigatorDrawer lessonKey={video.id} open={navOpen} onOpen={openNav} onClose={() => setNavOpen(false)} width={navWidth} tooltip={navTip}
          ribbonLabel={navPos ? `Open Course Navigator, lesson ${navPos}` : 'Open Course Navigator'} colors={colors} light={isLightTheme(currentTier)}>{navView('panel')}</NavigatorDrawer>
      )}
      {navLayout === 'phone' && (
        <PhoneNavigatorDrawer open={navOpen} onClose={() => setNavOpen(false)} width={Math.min(480, Math.round(page.width * 0.9))} closeTop={closeAt.top} closeRight={closeAt.right} returnFocusTo={courseBtnRef} colors={colors} light={isLightTheme(currentTier)}>{navView('phone')}</PhoneNavigatorDrawer>
      )}
      <UpgradePrompt open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        @media(hover:none)and(pointer:coarse){button:hover{opacity:1}}
      `}</style>
    </div>
  );
}
